// Each file's blobs at the points of a track: what a view asks the server
// for content-anchored state by (review marks and notes are looked up by
// path and blob, never by commit), so state written on an earlier blob of
// a file is found and carried to the lines shown by the line map.
//
// The blobs come from each commit's change list, not from asking about
// every file at every commit: a file's blob at the track's first point
// (its base) is asked for once, and each later point takes its parent's
// blobs with its own changed files applied (GitHub's
// `repos/o/r/commits/{sha}` `files[]`: the new blob, the path, the old path
// of a rename). That is one request per commit, however many files the
// view asks about, and it follows renames. A merge's changes are its first
// parent's comparison (`compare/{parent}...{merge}`), which is what the
// track's lane means. A point whose parent is not on the track (a commit a
// force-push replaced), or whose change list GitHub could not give whole
// (more files than it lists) or at all, has its blobs asked for directly,
// by `commit:path` GraphQL expressions, as the base's are. A change list
// GitHub answers it cannot give (404 or 422: a replaced commit it no longer
// has) is such a point for good, not a failure to ask about again.
//
// A commit never changes, so its change list and its blobs are kept for
// good: in memory for the page and in the account's IndexedDB
// (`commitChanges` and `treeBlobs`, each pruned oldest first past its
// limit, as the blob cache is). Only what GitHub proved is kept: a blob it
// named, or a file a commit it could see does not have; an error keeps
// nothing, so the pair is asked again next time. Concurrent callers share
// each request in flight, and every request goes through the page's
// scheduler at background priority. The commit and compare reads skip the
// HTTP response cache (`uncached`): their distilled change lists are what
// is kept. GitHub has no way to leave each file's `patch` out of either
// answer (neither endpoint takes a parameter for it, and the media types
// that drop it drop `files` too), so a commit's whole diff text is
// downloaded once, and only its change list kept.
//
// Cost, on a cold cache: one request per commit on the track (two for a
// merge) plus ⌈paths ÷ 100⌉ GraphQL queries for the base, and as many again
// for each point whose changes are not usable. A pull request of 30 commits
// opened on one file is about 31 requests; every other file of it then
// costs one base query and nothing per commit. A compare is the same, by
// its commits. A ref's window of N commits is N requests, and a window
// paged further back costs only its new commits.
import { graphqlResult, rest, restPages } from '../api/gh';
import { HttpError, LruMap } from '../api/cache';
import { idbGetMany, idbWrite } from '../api/db';
import { identityGeneration, onIdentityChange } from '../api/identity';
import { blobShasQuery } from '../api/queries';

/** One changed file of a commit: its path, its old path when it was renamed, and its new blob (null: deleted). */
export interface ChangedFile {
  path: string;
  from?: string;
  blob: string | null;
}

/** A commit's changes against its first parent; `files` null when GitHub could not list them all. */
export interface Changes {
  parent: string | null;
  files: ChangedFile[] | null;
  /** Not listed whole only because the reader stopped at its page cap: an uncapped read may list it, so it is kept nowhere. */
  capped?: true;
}

/** What blobsOnTrack asks GitHub, one call per question. */
export interface TrackSource {
  /** The changes of commit `sha` against its first parent. */
  changes(sha: string): Promise<Changes>;
  /** The blob of each `commit:path` expression: a SHA, null where GitHub proved there is no such file, undefined where its answer proves nothing. */
  blobsAt(expressions: string[]): Promise<(string | null | undefined)[]>;
}

/** Where answers are kept beyond the page (IndexedDB by default). */
export interface TrackStore {
  readChanges(keys: readonly string[]): Promise<(Changes | undefined)[]>;
  writeChanges(entries: readonly [string, Changes][]): Promise<void>;
  readBlobs(keys: readonly string[]): Promise<(string | null | undefined)[]>;
  writeBlobs(entries: readonly [string, string | null][]): Promise<void>;
}

/** Expressions asked of GitHub in one query. */
export const BLOBS_PER_QUERY = 100;
/** Files GitHub lists on one page of a commit (`repos/o/r/commits/{sha}`). */
export const COMMIT_FILES_PAGE = 300;
/** Files GitHub lists for one commit at most (`repos/o/r/commits/{sha}`, paged). */
export const COMMIT_FILES_MAX = 3000;
/** Files GitHub lists for one comparison at most (`compare`, not paged). */
export const COMPARE_FILES_MAX = 300;

interface RawFile {
  filename: string;
  previous_filename?: string;
  status: string;
  sha?: string | null;
}

/** GitHub's files as a change list: a deleted file has no blob, a renamed one its old path. */
function changedFiles(files: readonly RawFile[]): ChangedFile[] {
  return files.map((f) => ({
    path: f.filename,
    ...(f.status === 'renamed' && f.previous_filename ? { from: f.previous_filename } : {}),
    blob: f.status === 'removed' ? null : (f.sha ?? null),
  }));
}

/** How a source reads a commit (parents and every listed file, and whether that is all of them: `capped` when only its page cap stopped it) and a comparison. */
export interface CommitReader {
  commit(sha: string): Promise<{ parents: string[]; files: RawFile[]; complete: boolean; capped?: boolean }>;
  compare(base: string, head: string): Promise<{ files: RawFile[]; complete: boolean }>;
}

/** The changes of `sha` read through `read`: a merge's against its first parent, by a comparison; too many files, none. */
export async function changesOf(read: CommitReader, sha: string): Promise<Changes> {
  const c = await read.commit(sha);
  const parent = c.parents[0] ?? null;
  if (c.parents.length > 1) {
    const cmp = await read.compare(parent!, sha);
    return { parent, files: cmp.complete ? changedFiles(cmp.files) : null };
  }
  if (c.capped) return { parent, files: null, capped: true };
  return { parent, files: c.complete ? changedFiles(c.files) : null };
}

/**
 * GitHub as the source, for `owner/repo`. `commitPages` caps the pages of a
 * commit's file list read (30 by default, every page GitHub gives): a reader
 * that must stay within a request budget reads one, and a commit with more
 * files is read as one whose changes are not listed whole.
 */
export function githubSource(owner: string, repo: string, opts: { commitPages?: number } = {}): TrackSource {
  const maxPages = opts.commitPages ?? 30;
  const priority = 'background' as const;
  const read: CommitReader = {
    async commit(sha) {
      let parents: string[] | null = null;
      const files = await restPages<{ parents?: { sha: string }[]; files?: RawFile[] }, RawFile>(
        `repos/${owner}/${repo}/commits/${sha}`,
        (body) => {
          parents ??= (body.parents ?? []).map((p) => p.sha);
          return body.files ?? [];
        },
        { priority, maxPages, uncached: true },
      );
      // A full last page under the cap may have had more after it.
      const capped = maxPages < 30 && files.length >= maxPages * COMMIT_FILES_PAGE;
      return { parents: parents ?? [], files, complete: !capped && files.length < COMMIT_FILES_MAX, capped };
    },
    async compare(base, head) {
      const body = await rest<{ files?: RawFile[] }>(`repos/${owner}/${repo}/compare/${base}...${head}`, { priority, uncached: true });
      const files = body.files ?? [];
      return { files, complete: files.length < COMPARE_FILES_MAX };
    },
  };
  return {
    changes: (sha) => changesOf(read, sha),
    async blobsAt(expressions) {
      const vars: Record<string, unknown> = { owner, repo };
      expressions.forEach((e, i) => (vars['e' + i] = e));
      const { data, errors } = await graphqlResult<{ repository: Record<string, { oid?: string } | null> | null }>(
        blobShasQuery(expressions.length),
        vars,
        { priority },
      );
      // No repository: nothing is proven. An error naming an expression's field: that one proves nothing.
      if (!data?.repository) return expressions.map(() => undefined);
      const failed = new Set(errors.flatMap((e) => (e.path ?? []).filter((p): p is string => typeof p === 'string')));
      return expressions.map((_, i) => {
        if (failed.has('f' + i)) return undefined;
        return data.repository!['f' + i]?.oid ?? null;
      });
    },
  };
}

const idbStore: TrackStore = {
  async readChanges(keys) {
    return (await idbGetMany('commitChanges', keys)).map((r) => (r ? { parent: r.parent, files: r.files } : undefined));
  },
  async writeChanges(entries) {
    const at = Date.now();
    await idbWrite(
      'commitChanges',
      entries.map(([k, c]) => [k, { parent: c.parent, files: c.files, at }]),
    );
  },
  async readBlobs(keys) {
    return (await idbGetMany('treeBlobs', keys)).map((r) => (r === undefined ? undefined : r.blob));
  },
  async writeBlobs(entries) {
    const at = Date.now();
    await idbWrite(
      'treeBlobs',
      entries.map(([k, blob]) => [k, { blob, at }]),
    );
  },
};

/** The changes of a commit GitHub cannot read (`gone`): no parent known, so its blobs are asked for by name. */
const GONE: Changes = Object.freeze({ parent: null, files: null });

/** GitHub's answer that a commit's changes can never be read: 404 (no such commit) or 422 (no commit for that SHA). */
function gone(e: unknown): boolean {
  return e instanceof HttpError && (e.status === 404 || e.status === 422);
}

/** This page's copies, the signed-in account's: change lists by `<repo>@<sha>`, blobs by `<repo>@<commit>:<path>`. */
const pageChanges = new LruMap<string, Changes>(2000);
const pageBlobs = new LruMap<string, string | null>(20_000);
/** Requests in flight, by the same keys, shared by every caller. */
const flyingChanges = new Map<string, Promise<Changes | undefined>>();
const flyingBlobs = new Map<string, Promise<string | null | undefined>>();
onIdentityChange(forgetPageBlobs);

/**
 * Which of commits `shas` of `owner/repo` have no change list kept (in the
 * page, or the account's IndexedDB): what reading their blobs on a track
 * would ask GitHub for, one REST request each. No GitHub call.
 */
export async function uncachedChanges(owner: string, repo: string, shas: readonly string[], store: TrackStore = idbStore): Promise<string[]> {
  const repoKey = `${owner}/${repo}`.toLowerCase();
  const keys = shas.map((sha) => `${repoKey}@${sha}`);
  const missing = keys.flatMap((k, i) => (pageChanges.get(k) ? [] : [i]));
  const kept = await store.readChanges(missing.map((i) => keys[i])).catch(() => missing.map(() => undefined));
  return missing.filter((_, j) => !kept[j]).map((i) => shas[i]);
}

/** Forget this page's copies (another account; and tests: a fresh page over the same IndexedDB). */
export function forgetPageBlobs() {
  pageChanges.clear();
  pageBlobs.clear();
  flyingChanges.clear();
  flyingBlobs.clear();
}

/** One file's blobs on a track. */
export interface FileOnTrack {
  /**
   * Its blobs by the name it held each under, at the points where this
   * file held that name: a file that left a path (renamed or deleted) and
   * another made there later are two files, and neither has the other's.
   */
  blobs: Map<string, string[]>;
  /** Every change list and blob the answer needs was read; false when GitHub (or the cache) failed on some, so a caller asks again later. */
  complete: boolean;
}

/**
 * The blobs of each of `paths` at `points` (a track's commits, its base
 * first, each after its parent where the parent is on it), merged by name:
 * each path's file's blobs (`blobsOnTrackPerPath`), under every name it
 * had on the track (state stored under an old name is looked up by it). A
 * blob GitHub could not be asked about is left out (and asked again next
 * time), never guessed.
 */
export async function blobsOnTrack(
  owner: string,
  repo: string,
  points: readonly string[],
  paths: readonly string[],
  deps: { source?: TrackSource; store?: TrackStore; at?: string | null } = {},
): Promise<Map<string, string[]>> {
  const out = new Map<string, Set<string>>(paths.map((p) => [p, new Set()]));
  for (const file of (await blobsOnTrackPerPath(owner, repo, points, paths, deps)).values())
    for (const [name, blobs] of file.blobs) for (const b of blobs) (out.get(name) ?? out.set(name, new Set()).get(name)!).add(b);
  return new Map([...out].map(([n, b]) => [n, [...b]]));
}

/**
 * For each of `paths`, the file it names (at `at`, the commit whose tree
 * the view shows it from; by default the last of `points` holding the
 * path) followed along the track: its blobs by each name it held, at the
 * points where it held that name, across renames forward and backward.
 * A name is a file's only where the track shows the file there: changed
 * in place or renamed from the file's name at the point before. A point
 * whose change list is not known (its parent is not on the track, or
 * GitHub could not list it) takes the file by its name there. One set of
 * questions to GitHub serves every path (the base's blobs of every name in
 * one query of up to 100).
 */
export async function blobsOnTrackPerPath(
  owner: string,
  repo: string,
  points: readonly string[],
  paths: readonly string[],
  deps: { source?: TrackSource; store?: TrackStore; at?: string | null } = {},
): Promise<Map<string, FileOnTrack>> {
  const t = await onTrack(owner, repo, points, paths, deps);
  return new Map(paths.map((path) => [path, { blobs: t.fileOf(path, deps.at ?? null), complete: t.complete }]));
}

/** Every point's blob of every name the files had, what links them, and whether all of it was read. */
async function onTrack(
  owner: string,
  repo: string,
  points: readonly string[],
  paths: readonly string[],
  deps: { source?: TrackSource; store?: TrackStore },
): Promise<{ complete: boolean; fileOf(path: string, at: string | null): Map<string, string[]> }> {
  const source = deps.source ?? githubSource(owner, repo);
  const store = deps.store ?? idbStore;
  const repoKey = `${owner}/${repo}`.toLowerCase();
  const generation = identityGeneration();
  const current = () => identityGeneration() === generation;
  /** Something the answer needed could not be read. */
  let failed = false;

  const changesFor = async (sha: string): Promise<Changes | undefined> => {
    const k = `${repoKey}@${sha}`;
    const hit = pageChanges.get(k);
    if (hit) return hit;
    let flying = flyingChanges.get(k);
    if (!flying) {
      flying = (async () => {
        // A cache that cannot be read is a miss: GitHub is asked.
        const [kept] = await store.readChanges([k]).catch(() => [undefined]);
        if (kept) return kept;
        try {
          const c = await source.changes(sha);
          if (current() && !c.capped) await store.writeChanges([[k, c]]).catch(() => {});
          return c;
        } catch (e) {
          // GitHub has no such commit (any more): its blobs are read by name, as a point off the track's.
          // Kept for the page only: that proves nothing about the commit's changes.
          if (gone(e)) return GONE;
          console.warn('a commit’s changes could not be read', sha, e);
          return undefined;
        }
      })().finally(() => flyingChanges.delete(k));
      flyingChanges.set(k, flying);
    }
    const c = await flying;
    if (c && current() && !c.capped) pageChanges.set(k, c);
    return c;
  };

  /** The blob of each name at `commit` (undefined: not known). */
  const blobsAt = async (commit: string, names: readonly string[]): Promise<Map<string, string | null | undefined>> => {
    const key = (name: string) => `${repoKey}@${commit}:${name}`;
    const out = new Map<string, string | null | undefined>();
    const unknown = names.filter((n) => {
      const hit = pageBlobs.get(key(n));
      if (hit !== undefined) out.set(n, hit);
      return hit === undefined;
    });
    const fresh = unknown.filter((n) => !flyingBlobs.has(key(n)));
    if (fresh.length) {
      // One read of IndexedDB, then GitHub for the rest, 100 to a query; each name's promise is shared meanwhile.
      const settle = new Map<string, (v: string | null | undefined) => void>();
      for (const n of fresh) flyingBlobs.set(key(n), new Promise((r) => settle.set(n, r)));
      const done = (n: string, v: string | null | undefined) => {
        if (!settle.has(n)) return;
        if (v !== undefined && current()) pageBlobs.set(key(n), v);
        flyingBlobs.delete(key(n));
        settle.get(n)!(v);
        settle.delete(n);
      };
      void (async () => {
        const kept = await store.readBlobs(fresh.map(key)).catch(() => fresh.map(() => undefined));
        const ask = fresh.filter((n, i) => {
          if (kept[i] === undefined) return true;
          done(n, kept[i]);
          return false;
        });
        for (let i = 0; i < ask.length; i += BLOBS_PER_QUERY) {
          const chunk = ask.slice(i, i + BLOBS_PER_QUERY);
          let got: (string | null | undefined)[];
          try {
            got = await source.blobsAt(chunk.map((n) => `${commit}:${n}`));
          } catch (e) {
            console.warn('blobs at a commit could not be read', commit, e);
            got = chunk.map(() => undefined);
          }
          const proven = chunk.flatMap((n, j): [string, string | null][] => (got[j] === undefined ? [] : [[key(n), got[j]!]]));
          if (proven.length && current()) await store.writeBlobs(proven).catch(() => {});
          chunk.forEach((n, j) => done(n, got[j]));
        }
      })().catch(() => {
        // Whatever is still waiting is not known.
        for (const n of fresh) done(n, undefined);
      });
    }
    await Promise.all(
      unknown.map(async (n) => {
        out.set(n, await flyingBlobs.get(key(n)));
      }),
    );
    return out;
  };

  if (!points.length || !paths.length) return { complete: true, fileOf: () => new Map() };

  // Every commit's changes, at once; then every name the files may have had on the track.
  const changes = new Map<string, Changes | undefined>();
  await Promise.all(points.slice(1).map(async (sha) => changes.set(sha, await changesFor(sha))));
  for (const c of changes.values()) if (!c) failed = true;
  // The names to ask about: every name a rename links to one of `paths` (a superset of each file's own).
  const lineage = new Set<string>(paths);
  for (let grew = true; grew; ) {
    grew = false;
    for (const c of changes.values())
      for (const f of c?.files ?? []) {
        if (!f.from || (!lineage.has(f.path) && !lineage.has(f.from))) continue;
        for (const name of [f.path, f.from])
          if (!lineage.has(name)) {
            lineage.add(name);
            grew = true;
          }
      }
  }
  const names = [...lineage];

  // Each point's blob of each name: the base's asked; each later point's derived from its parent's when it can be.
  const state = new Map<string, Map<string, string | null | undefined>>();
  const linked = new Set<string>();
  const ask = async (sha: string) => {
    const got = await blobsAt(sha, names);
    for (const v of got.values()) if (v === undefined) failed = true;
    return got;
  };
  state.set(points[0], await ask(points[0]));
  for (const sha of points.slice(1)) {
    const c = changes.get(sha);
    const before = c?.parent ? state.get(c.parent) : undefined;
    if (c?.files && before) {
      const s = new Map(before);
      for (const f of c.files) {
        if (f.from && f.from !== f.path && lineage.has(f.from)) s.set(f.from, null);
        if (lineage.has(f.path)) s.set(f.path, f.blob);
      }
      state.set(sha, s);
      linked.add(sha);
    } else state.set(sha, await ask(sha));
  }

  // Which (point, name) are one file across a point and its parent: a
  // name kept or changed in place, or a rename's old name and new name.
  // A name added at a point (absent at its parent) starts a new file.
  const edges: [string, string][] = [];
  const node = (sha: string, name: string) => `${sha}\0${name}`;
  for (const sha of points.slice(1)) {
    const c = changes.get(sha);
    const parent = c?.parent;
    const before = parent ? state.get(parent) : undefined;
    const after = state.get(sha)!;
    if (!c || !parent || !before) continue;
    if (linked.has(sha)) {
      const renamedAway = new Set(c.files!.flatMap((f) => (f.from && f.from !== f.path ? [f.from] : [])));
      const touched = new Map(c.files!.map((f) => [f.path, f]));
      for (const n of names) {
        if (!before.get(n) || !after.get(n)) continue;
        const f = touched.get(n);
        // Changed in place, or not changed: the same file. A path renamed away and made again here is a new file.
        if (renamedAway.has(n) && (!f || !f.from || f.from === n)) continue;
        if (f?.from && f.from !== n) continue;
        edges.push([node(parent, n), node(sha, n)]);
      }
      for (const f of c.files!) if (f.from && f.from !== f.path && before.get(f.from) && after.get(f.path)) edges.push([node(parent, f.from), node(sha, f.path)]);
    } else if (c.files === null) {
      // GitHub could not list its changes (too many files): the same name is the same file.
      for (const n of names) if (before.get(n) && after.get(n)) edges.push([node(parent, n), node(sha, n)]);
    }
  }

  return {
    complete: !failed,
    fileOf(path, at) {
      // Union-find over (point, name), with points whose parent is not on
      // the track joined to the file by its name at the point it is read at.
      const up = new Map<string, string>();
      const find = (x: string): string => {
        let r = x;
        while (up.has(r) && up.get(r) !== r) r = up.get(r)!;
        up.set(x, r);
        return r;
      };
      const join = (a: string, b: string) => {
        const [ra, rb] = [find(a), find(b)];
        if (ra !== rb) up.set(ra, rb);
      };
      for (const [a, b] of edges) join(a, b);
      const holds = (sha: string, name: string) => !!state.get(sha)?.get(name);
      const free = (sha: string) => {
        const c = changes.get(sha);
        return sha !== points[0] && (!c || !c.parent || !state.has(c.parent));
      };
      // The file as read: at `at`, else at the last point linked to the track holding the path, else the last holding it.
      const back = [...points].reverse();
      const from =
        at && points.includes(at) && holds(at, path) ? at : (back.find((p) => !free(p) && holds(p, path)) ?? back.find((p) => holds(p, path)));
      if (!from) return new Map([[path, []]]);
      for (const sha of points) if (sha !== from && free(sha) && holds(sha, path)) join(node(sha, path), node(from, path));
      const mine = find(node(from, path));
      const out = new Map<string, Set<string>>([[path, new Set()]]);
      for (const sha of points)
        for (const [name, blob] of state.get(sha)!)
          if (blob && find(node(sha, name)) === mine) (out.get(name) ?? out.set(name, new Set()).get(name)!).add(blob);
      return new Map([...out].map(([n, b]) => [n, [...b]]));
    },
  };
}
